import { useState, FC, useRef, useEffect } from "react"
import { useModal } from "../../hooks/useModal"
import useUpdateMenuSection from '../../hooks/MenuSection/useUpdateMenuSection'
import useDeleteMenuSection from '../../hooks/MenuSection/useDeleteMenuSection'
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import { Modal } from "../../components/ui/modal";
import ComponentCard from "../../components/common/ComponentCard";
import { useToast } from "../../context/ToastContext";

import type { MenuSection } from "../../types/menuSectionTypes";

type MenuSectionEditProps = {
  menuSection: MenuSection
  onUpdate: (section: MenuSection) => void
  onDelete: (section: MenuSection) => void
}

const MenuSectionEdit: FC<MenuSectionEditProps> = ({ menuSection, onUpdate, onDelete }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { updateMenuSection } = useUpdateMenuSection({
    onCompleted: (menuSectionUpdated) => {
      if (menuSectionUpdated) {
        onUpdate(menuSectionUpdated as MenuSection);
        setEdit(false)
        toast.success(`Section ${menuSectionUpdated.name} updated successfully`);
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }
  });

  const { deleteMenuSection } = useDeleteMenuSection({
    onCompleted: (menuSectionDeleted) => {
      if (menuSectionDeleted) {
        onDelete(menuSectionDeleted as MenuSection);
        toast.success(`Section ${menuSectionDeleted.name} deleted successfully`);
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }
  });

  const [edit, setEdit] = useState<boolean>(false)
  const [name, setName] = useState<string>(menuSection.name)
  const [hintNameInput, setHintNameInput] = useState<string>("")
  const [errorNameInput, setErrorNameInput] = useState<boolean>(false)
  const { isOpen, openModal, closeModal } = useModal();

  const saveSection = async () => {
    if (name === '') {
      setErrorNameInput(true)
      setHintNameInput("Can't be empty string")
      return
    } else if (name === menuSection.name) {
      setEdit(false)
      return
    }

    try {
      await updateMenuSection({
        id: menuSection.id!,
        position: menuSection.position,
        menuId: menuSection.menuId,
        name: name 
      });
    } catch (err) {
      console.error("menu section update error (component):", err);
      // toast
    }
  }

  const deleteSection = async () => {
    try {
      await deleteMenuSection({
        id: menuSection.id!
      });
    } catch (err) {
      console.error("menu section delete error (component):", err);
      // toast
    }
  }

  useEffect(() => {
    if (edit) {
      inputRef.current?.focus();
    }  
  }, [edit]);

  return (
    <div key={`menuSection-${menuSection.id}`} className="flex">
        {edit 
          ? <Input
              className="h-12"
              parentClassName="w-full"
              placeholder="Name for new section"
              value={name}
              id={`menuSection.${menuSection.id}.name`}
              hint={hintNameInput}
              error={errorNameInput}
              ref={inputRef}
              onChange={(e) => {
                if (e.target.value !== '') {
                  setErrorNameInput(false)
                  setHintNameInput("")
                }
                setName(e.target.value)
              }}  
            />
          : <div className="w-full rounded-lg border px-3 py-3 text-sm shadow-theme-xs placeholder:text-gray-400 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 text-gray-800 border-gray-300 dark:border-gray-700">
              <span className="text-md h-12 text-left text-gray-500 dark:text-gray-400">
                {menuSection.name}
              </span>  
            </div>
        }

      { edit   
      ? <Button
          variant='primary'
          className="ml-5 h-12"
          onClick={saveSection}
        >
          Save
        </Button>
      : <Button
          variant='warning'
          className="ml-5 h-12"
          onClick={() => {
            setEdit(true)
            inputRef.current?.focus();
          }}
        >
          Edit
        </Button>
      } 

      { edit 
      ? <Button
          variant="outline"
          className="ml-5 h-12"
          onClick={
            () => setEdit(false)
          }
        >
          Cancel
        </Button>
      : <Button
          variant="delete"
          className="ml-5 h-12"
          onClick={openModal}
        >
          Delete
        </Button>
      }

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        showCloseButton
        className="max-w-[700px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <ComponentCard 
            className="text-center"
            title={`You want delete section ${name}, are you sure?`}
          >
            <div>
              <Button
                variant="primary"
                className="ml-5 h-12"
                onClick={() => {
                  deleteSection()
                  closeModal()
                }}
              >
                Delete
              </Button>
              <Button
                variant="delete"
                className="ml-5 h-12"
                onClick={closeModal}
              >
                Cancel
              </Button>
            </div>
          </ComponentCard>
      </Modal>
    </div>
  )
}

export default MenuSectionEdit
